import * as React from 'react';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio as RadioGroupButton,
  RadioGroup as MatRadioGroup,
  SxProps,
  Theme
} from '@mui/material';

type RadioGroupColor = 'primary' | 'secondary' | 'default';
type MarginType = 'dense' | 'normal' | 'none';
export interface RadioGroupOption {
  id: string;
  color?: string;
  description: string;
  isEnabled: boolean;
}

export interface RadioGroupProps {
  options: RadioGroupOption[];
  label?: string;
  helperText?: string;
  selectedOptionId: string;
  onChange: (event: any) => void;
  required?: boolean;
  inputRef?: any;
  margin?: MarginType;
  fullWidth?: boolean;
  horizontal?: boolean;
  ariaControls?: string;
  dataProp?: string;
  sx?: SxProps<Theme>;
}

const BaseRadioGroup: React.FunctionComponent<RadioGroupProps> = ({
  helperText,
  label,
  options,
  selectedOptionId,
  fullWidth,
  margin,
  horizontal,
  ariaControls,
  dataProp = 'radio-option',
  ...rest
}: RadioGroupProps) => {
  return (
    <FormControl fullWidth={fullWidth} {...rest} margin={margin}>
      {label && (
        <FormLabel focused={false} component="legend">
          {label}
        </FormLabel>
      )}
      <MatRadioGroup
        aria-label={label}
        aria-controls={ariaControls}
        name={`radiogroup-name-${label || 'no-name-provided'}`}
        className={horizontal ? 'radioGroupHorizontal ' : undefined}
      >
        {options.map(({ id, isEnabled, color, description }: RadioGroupOption) => (
          <FormControlLabel
            control={
              <RadioGroupButton
                checked={id === selectedOptionId}
                color={color as RadioGroupColor}
                disabled={!isEnabled}
                inputProps={
                  {
                    checked: id === selectedOptionId,
                    'data-selector': `${dataProp}-${id}`
                  } as React.InputHTMLAttributes<HTMLInputElement>
                }
              />
            }
            value={id}
            label={description}
            key={id}
          />
        ))}
      </MatRadioGroup>
      {helperText && <FormHelperText style={{ marginLeft: '0' }}>{helperText}</FormHelperText>}
    </FormControl>
  );
};

BaseRadioGroup.defaultProps = {
  margin: 'normal',
  fullWidth: true
};

export const arePropsEqual = (prev: RadioGroupProps, next: RadioGroupProps) => {
  const { options: prevOptions } = prev;
  const { options: nextOptions } = next;
  const optionsAreEqual = JSON.stringify(prevOptions) === JSON.stringify(nextOptions);
  const propsAreEqual = JSON.stringify(prev) === JSON.stringify(next);
  return optionsAreEqual && propsAreEqual;
};

const RadioGroup = React.memo(BaseRadioGroup, arePropsEqual);
RadioGroup.displayName = 'RadioGroup';
export { RadioGroup };
